import type { OccasionDefinition, OccasionKind, OccasionRecurrence } from '@unipostudio/shared-types';

/**
 * Curated occasions for the calendar (blueprint S20, M10, A11): public
 * holidays and the moments a shop, salon or restaurant would post about, for
 * NL, DE, FR, ES, IT, BE, PT, IE, GB and IN, the Gulf and the wider Arab world
 * (SA, AE, QA, KW, BH, OM, EG, JO, MA), plus global retail moments and industry
 * days. The rest of Europe, the USA, Canada and Australia come from the
 * imported public-holiday feed. Names are English data; the UI layer translates labels.
 *
 * Rules of the file:
 * - Accuracy over volume. Calendar-based dates (Diwali, Eid, Holi...) are
 *   tables for 2026-2030, each checked against at least two published sources
 *   in September 2026 (Umm al-Qura tables for Islamic dates, drikpanchang and
 *   the Indian government holiday lists for Hindu ones). Anything derived from
 *   an Easter or weekday rule is a recurrence. Dates we could not confirm are
 *   left out; Islamic dates can still move a day with the moon sighting.
 * - `countries: []` means everywhere. Global entries may still carry
 *   countries when the day differs per country (Mother's Day).
 * - `regions` restricts inside a country (ISO 3166-2, e.g. `DE-BY`); a
 *   workspace only sees those when its region matches.
 * - `whyItMatters` is one plain sentence for a small business owner, sentence
 *   case, no exclamation marks.
 */

const SUN = 0;
const MON = 1;
const TUE = 2;
const WED = 3;
const THU = 4;
const FRI = 5;
const SAT = 6;

const fixed = (month: number, day: number): OccasionRecurrence => ({ type: 'fixed', month, day });
const nth = (month: number, weekday: number, n: number): OccasionRecurrence => ({ type: 'nth_weekday', month, weekday, n });
const easter = (days: number): OccasionRecurrence => ({ type: 'easter_offset', days });
const table = (dates: Record<string, string>): OccasionRecurrence => ({ type: 'table', dates });

interface Extra {
  regions?: string[];
  excludeCountries?: string[];
  industries?: string[];
  durationDays?: number;
}

function entry(
  id: string,
  name: string,
  emoji: string,
  scope: OccasionDefinition['scope'],
  kind: OccasionKind,
  countries: string[],
  recurrence: OccasionRecurrence,
  leadDays: number,
  whyItMatters: string,
  extra: Extra = {},
): OccasionDefinition {
  return { id, name, emoji, scope, kind, countries, recurrence, leadDays, whyItMatters, ...extra };
}

/** Global moment, everywhere unless `countries` is given (per-country days). */
const global = (
  id: string,
  name: string,
  emoji: string,
  kind: OccasionKind,
  recurrence: OccasionRecurrence,
  leadDays: number,
  why: string,
  countries: string[] = [],
  extra: Extra = {},
): OccasionDefinition => entry(id, name, emoji, 'global', kind, countries, recurrence, leadDays, why, extra);

/** Regional public holiday. */
const holiday = (
  id: string,
  name: string,
  emoji: string,
  countries: string[],
  recurrence: OccasionRecurrence,
  leadDays: number,
  why: string,
  extra: Extra = {},
): OccasionDefinition => entry(id, name, emoji, 'regional', 'public_holiday', countries, recurrence, leadDays, why, extra);

/** Regional cultural, retail or observance moment. */
const regional = (
  id: string,
  name: string,
  emoji: string,
  kind: OccasionKind,
  countries: string[],
  recurrence: OccasionRecurrence,
  leadDays: number,
  why: string,
  extra: Extra = {},
): OccasionDefinition => entry(id, name, emoji, 'regional', kind, countries, recurrence, leadDays, why, extra);

/** Industry day, everywhere, for the listed workspace industries. */
const industry = (
  id: string,
  name: string,
  emoji: string,
  industries: string[],
  recurrence: OccasionRecurrence,
  leadDays: number,
  why: string,
  extra: Omit<Extra, 'industries'> = {},
): OccasionDefinition => entry(id, name, emoji, 'industry', 'industry', [], recurrence, leadDays, why, { ...extra, industries });

/** Day after the fourth Thursday of November (US Thanksgiving). nth_weekday cannot add a day, so a table. */
const BLACK_FRIDAY: Record<string, string> = {
  '2026': '2026-11-27',
  '2027': '2027-11-26',
  '2028': '2028-11-24',
  '2029': '2029-11-23',
  '2030': '2030-11-29',
};
const SMALL_BUSINESS_SATURDAY: Record<string, string> = {
  '2026': '2026-11-28',
  '2027': '2027-11-27',
  '2028': '2028-11-25',
  '2029': '2029-11-24',
  '2030': '2030-11-30',
};
const CYBER_MONDAY: Record<string, string> = {
  '2026': '2026-11-30',
  '2027': '2027-11-29',
  '2028': '2028-11-27',
  '2029': '2029-11-26',
  '2030': '2030-12-02',
};
const GIVING_TUESDAY: Record<string, string> = {
  '2026': '2026-12-01',
  '2027': '2027-11-30',
  '2028': '2028-11-28',
  '2029': '2029-11-27',
  '2030': '2030-12-03',
};

/** Makar Sankranti / Pongal follow the solar calendar: 14 January, slipping to the 15th in some years. */
// 2027: the Sun enters Makara after sunset on 14 January, so the festival day is the 15th.
const SANKRANTI: Record<string, string> = {
  '2026': '2026-01-14', '2027': '2027-01-15', '2028': '2028-01-15', '2029': '2029-01-14', '2030': '2030-01-14',
};
// Tamil Nadu kept Thai Pongal on the 15th in 2026, a day after Sankranti; later
// years disagree between sources, so only the confirmed ones are listed.
const PONGAL: Record<string, string> = { '2026': '2026-01-15', '2027': '2027-01-15' };

/* Islamic calendar, Umm al-Qura (Saudi Arabia, UAE). Other countries may keep a day later. */
const RAMADAN: Record<string, string> = {
  '2026': '2026-02-18', '2027': '2027-02-08', '2028': '2028-01-28', '2029': '2029-01-16', '2030': '2030-01-05',
};
/** 1452 AH begins inside 2030 as well, so 2030 has two Ramadans. */
const RAMADAN_SECOND: Record<string, string> = { '2030': '2030-12-26' };
const EID_AL_FITR: Record<string, string> = {
  '2026': '2026-03-20', '2027': '2027-03-09', '2028': '2028-02-26', '2029': '2029-02-14', '2030': '2030-02-04',
};
const DAY_OF_ARAFAT: Record<string, string> = {
  '2026': '2026-05-26', '2027': '2027-05-15', '2028': '2028-05-04', '2029': '2029-04-23', '2030': '2030-04-12',
};
const EID_AL_ADHA: Record<string, string> = {
  '2026': '2026-05-27', '2027': '2027-05-16', '2028': '2028-05-05', '2029': '2029-04-24', '2030': '2030-04-13',
};
const ISLAMIC_NEW_YEAR: Record<string, string> = {
  '2026': '2026-06-16', '2027': '2027-06-06', '2028': '2028-05-25', '2029': '2029-05-14', '2030': '2030-05-03',
};
const MAWLID: Record<string, string> = {
  '2026': '2026-08-25', '2027': '2027-08-14', '2028': '2028-08-03', '2029': '2029-07-24', '2030': '2030-07-13',
};
const ISRA_MIRAJ: Record<string, string> = {
  '2026': '2026-01-16', '2027': '2027-01-05', '2028': '2028-12-14', '2029': '2029-12-03', '2030': '2030-11-23',
};
/** 27 Rajab falls twice in 2027. */
const ISRA_MIRAJ_SECOND: Record<string, string> = { '2027': '2027-12-25' };
const CHINESE_NEW_YEAR: Record<string, string> = {
  '2026': '2026-02-17', '2027': '2027-02-06', '2028': '2028-01-26', '2029': '2029-02-13', '2030': '2030-02-03',
};
/** The Monday after Orthodox Easter. */
const SHAM_EL_NESSIM: Record<string, string> = {
  '2026': '2026-04-13', '2027': '2027-05-03', '2028': '2028-04-17', '2029': '2029-04-09', '2030': '2030-04-29',
};

/* Hindu calendar, as observed in India (New Delhi; Durga Puja for Kolkata). */
const HOLI: Record<string, string> = {
  '2026': '2026-03-04', '2027': '2027-03-22', '2028': '2028-03-11', '2029': '2029-03-01', '2030': '2030-03-20',
};
const VASANT_PANCHAMI: Record<string, string> = {
  '2026': '2026-01-23', '2027': '2027-02-11', '2028': '2028-01-31', '2029': '2029-01-19', '2030': '2030-02-07',
};
const MAHA_SHIVARATRI: Record<string, string> = {
  '2026': '2026-02-15', '2027': '2027-03-06', '2028': '2028-02-23', '2029': '2029-02-11', '2030': '2030-03-02',
};
const UGADI: Record<string, string> = {
  '2026': '2026-03-19', '2027': '2027-04-07', '2028': '2028-03-27', '2029': '2029-04-14', '2030': '2030-04-03',
};
const RAM_NAVAMI: Record<string, string> = {
  '2026': '2026-03-26', '2027': '2027-04-15', '2028': '2028-04-03', '2029': '2029-04-23', '2030': '2030-04-12',
};
const BAISAKHI: Record<string, string> = {
  '2026': '2026-04-14', '2027': '2027-04-14', '2028': '2028-04-13', '2029': '2029-04-14', '2030': '2030-04-14',
};
const RONGALI_BIHU: Record<string, string> = { '2026': '2026-04-14', '2027': '2027-04-14', '2028': '2028-04-14' };
const RAKSHA_BANDHAN: Record<string, string> = {
  '2026': '2026-08-28', '2027': '2027-08-17', '2028': '2028-08-05', '2029': '2029-08-23', '2030': '2030-08-13',
};
const JANMASHTAMI: Record<string, string> = {
  '2026': '2026-09-04', '2027': '2027-08-25', '2028': '2028-08-13', '2029': '2029-09-01', '2030': '2030-08-21',
};
const GANESH_CHATURTHI: Record<string, string> = {
  '2026': '2026-09-14', '2027': '2027-09-04', '2028': '2028-08-23', '2029': '2029-09-11', '2030': '2030-09-01',
};
const ONAM: Record<string, string> = {
  '2026': '2026-08-26', '2027': '2027-09-12', '2028': '2028-09-01', '2029': '2029-08-22', '2030': '2030-09-09',
};
const NAVRATRI: Record<string, string> = {
  '2026': '2026-10-11', '2027': '2027-09-30', '2028': '2028-09-19', '2029': '2029-10-08', '2030': '2030-09-28',
};
/** Maha Saptami, Kolkata; the four days run to Vijaya Dashami. */
const DURGA_PUJA: Record<string, string> = {
  '2026': '2026-10-18', '2027': '2027-10-06', '2028': '2028-09-25', '2029': '2029-10-13', '2030': '2030-10-03',
};
const DUSSEHRA: Record<string, string> = {
  '2026': '2026-10-20', '2027': '2027-10-09', '2028': '2028-09-27', '2029': '2029-10-16', '2030': '2030-10-06',
};
const KARVA_CHAUTH: Record<string, string> = {
  '2026': '2026-10-29', '2027': '2027-10-18', '2028': '2028-10-07', '2029': '2029-10-26', '2030': '2030-10-15',
};
const DHANTERAS: Record<string, string> = {
  '2026': '2026-11-06', '2027': '2027-10-27', '2028': '2028-10-15', '2029': '2029-11-04', '2030': '2030-10-24',
};
const DIWALI: Record<string, string> = {
  '2026': '2026-11-08', '2027': '2027-10-29', '2028': '2028-10-17', '2029': '2029-11-05', '2030': '2030-10-26',
};
const BHAI_DOOJ: Record<string, string> = {
  '2026': '2026-11-11', '2027': '2027-10-31', '2028': '2028-10-19', '2029': '2029-11-07', '2030': '2030-10-28',
};
/** The evening offering (Sandhya Arghya), the main day. */
const CHHATH: Record<string, string> = {
  '2026': '2026-11-15', '2027': '2027-11-04', '2028': '2028-10-23', '2029': '2029-11-11', '2030': '2030-11-01',
};
const GURU_NANAK_JAYANTI: Record<string, string> = {
  '2026': '2026-11-24', '2027': '2027-11-14', '2028': '2028-11-02', '2029': '2029-11-21', '2030': '2030-11-10',
};

/*
 * Kerala, Tamil and Bengali new years follow the solar sankranti (Sun into
 * Mesha / Simha) and each state's own rule for which day it falls on.
 * 2026 and 2027 are the state holiday lists (Kerala G.O.(P) 15/2025/GAD:
 * Vishu 15 April 2026) and drikpanchang; 2028-2030 repeat the observance of
 * four years earlier, since the sankranti moves only ~37 minutes in four
 * years (2024: Vishu, Puthandu and Poila Boishakh all on 14 April).
 */
const VISHU: Record<string, string> = {
  '2026': '2026-04-15', '2027': '2027-04-15', '2028': '2028-04-14', '2029': '2029-04-14', '2030': '2030-04-15',
};
const PUTHANDU: Record<string, string> = {
  '2026': '2026-04-14', '2027': '2027-04-14', '2028': '2028-04-14', '2029': '2029-04-14', '2030': '2030-04-14',
};
const POILA_BOISHAKH: Record<string, string> = {
  '2026': '2026-04-15', '2027': '2027-04-15', '2028': '2028-04-14', '2029': '2029-04-15', '2030': '2030-04-15',
};
/** Chingam 1, the first day of the Malayalam (Kollavarsham) year: 17 August in every recent year. */
const CHINGAM_ONE: Record<string, string> = {
  '2026': '2026-08-17', '2027': '2027-08-17', '2028': '2028-08-17', '2029': '2029-08-17', '2030': '2030-08-17',
};
/** Lunar (Vaishakha Shukla Tritiya); drikpanchang, New Delhi. Later years are added once two sources agree. */
const AKSHAYA_TRITIYA: Record<string, string> = { '2026': '2026-04-19', '2027': '2027-05-09', '2028': '2028-04-27' };

/*
 * Islamic days as India keeps them: the central government's gazetted
 * holiday lists (DoP&T O.M. 12/2/2023-JCA for 2026 and 2027). India usually
 * sights the moon a day after the Umm al-Qura table; years without a
 * published list use the Umm al-Qura date plus that day, and move with the
 * sighting. Add each year's DoP&T list (published every July) here.
 */
const IN_EID_AL_FITR: Record<string, string> = { '2026': '2026-03-21', '2027': '2027-03-10' };
const IN_EID_AL_ADHA: Record<string, string> = { '2026': '2026-05-27', '2027': '2027-05-17' };
/** Muharram (Ashura, 10 Muharram). */
const IN_MUHARRAM: Record<string, string> = {
  '2026': '2026-06-26', '2027': '2027-06-16', '2028': '2028-06-04', '2029': '2029-05-24', '2030': '2030-05-13',
};
/** Milad-un-Nabi (Id-e-Milad). */
const IN_MILAD_UN_NABI: Record<string, string> = {
  '2026': '2026-08-26', '2027': '2027-08-15', '2028': '2028-08-04', '2029': '2029-07-25', '2030': '2030-07-14',
};

const MOON = ' Dates follow the moon sighting and can move by a day.';

/** The Gulf and the wider Arab world. */
const ARAB_COUNTRIES = ['SA', 'AE', 'QA', 'KW', 'BH', 'OM', 'EG', 'JO', 'MA'];
const GULF = ['SA', 'AE', 'QA', 'KW', 'BH', 'OM'];

/* ------------------------------------------------------------------ */
/* Global                                                              */
/* ------------------------------------------------------------------ */

const GLOBAL: OccasionDefinition[] = [
  global('global-new-years-day', "New Year's Day", '🎉', 'public_holiday', fixed(1, 1), 5,
    'Nearly everyone is off work and scrolling, so post your opening hours and a fresh-start offer.'),
  global('global-january-sales', 'January sales', '🏷️', 'retail', fixed(1, 2), 7,
    'Post-Christmas bargain hunting starts now, so announce your clearance before the shop next door does.',
    // Not India: its big sales follow its own festivals (PC-34c).
    ['NL', 'DE', 'IT', 'PT', 'IE', 'GB']),
  global('global-valentines-day', "Valentine's Day", '💝', 'retail', fixed(2, 14), 10,
    'Couples book tables, gifts and treatments in the week before, so promote early and take bookings.'),
  global('global-womens-day', "International Women's Day", '💐', 'observance', fixed(3, 8), 5,
    'A day to celebrate the women on your team and among your customers, with a mimosa sprig in Italy.'),
  global('global-april-fools', "April Fools' Day", '🃏', 'cultural', fixed(4, 1), 3,
    'A playful fake product or a light-hearted post gets shared, as long as the joke is obvious.'),
  global('global-easter-sunday', 'Easter Sunday', '🐣', 'cultural', easter(0), 10,
    'Family meals, chocolate and a long weekend, and most shops close on the Sunday itself.',
    // India keeps its own Easter (in-easter): shops there stay open.
    [], { excludeCountries: ['IN'] }),
  global('global-earth-day', 'Earth Day', '🌍', 'observance', fixed(4, 22), 5,
    'Customers like to hear what you do for the planet, from packaging to sourcing locally.'),

  // Mother's Day differs by country.
  global('global-mothers-day-mothering-sunday', "Mother's Day (Mothering Sunday)", '💐', 'retail', easter(-21), 10,
    'Flowers, cards, brunch and pamper gifts sell out the week before, so open bookings early.',
    ['GB', 'IE']),
  global('global-mothers-day-second-sunday-may', "Mother's Day", '💐', 'retail', nth(5, SUN, 2), 10,
    'Flowers, cards, brunch and pamper gifts sell out the week before, so open bookings early.',
    ['NL', 'DE', 'IT', 'BE', 'IN']),
  global('global-mothers-day-first-sunday-may', "Mother's Day", '💐', 'retail', nth(5, SUN, 1), 10,
    'Flowers, cards, lunch and pamper gifts sell out the week before, so open bookings early.',
    ['ES', 'PT']),
  global('global-mothers-day-fr', "Mother's Day (Fête des Mères)", '💐', 'retail', nth(5, SUN, -1), 10,
    'Flowers, cards, lunch and pamper gifts sell out the week before, so open bookings early.',
    ['FR']),

  // Father's Day differs by country.
  global('global-fathers-day-third-sunday-june', "Father's Day", '👔', 'retail', nth(6, SUN, 3), 10,
    'Gifts for dads are bought late, so a reminder in the final week converts well.',
    ['NL', 'FR', 'IE', 'GB', 'IN']),
  global('global-fathers-day-de', "Father's Day (Vatertag)", '🍻', 'retail', easter(39), 7,
    'Falls on Ascension Day, a public holiday that many spend outdoors with friends and a beer.',
    ['DE']),
  global('global-fathers-day-st-joseph', "Father's Day (St Joseph's Day)", '👔', 'retail', fixed(3, 19), 7,
    'Celebrated on St Joseph’s Day, with sweet pastries and family lunches on the menu.',
    ['ES', 'IT', 'PT']),
  global('global-fathers-day-be', "Father's Day", '👔', 'retail', nth(6, SUN, 2), 10,
    'Gifts for dads are bought late, so a reminder in the final week converts well.',
    ['BE']),

  global('global-pride-month', 'Pride Month', '🏳️‍🌈', 'observance', fixed(6, 1), 7,
    'Pride runs all June; a sincere post or a rainbow window works when it matches how you treat people.',
    [], { durationDays: 30 }),
  global('global-summer-sale', 'Summer sales start', '🌞', 'retail', fixed(7, 1), 7,
    'Summer sales open at the start of July across much of Europe, and shoppers are already hunting for deals.',
    [], { excludeCountries: ['IN'] }),
  global('global-back-to-school', 'Back to school', '🎒', 'retail', fixed(9, 1), 14,
    'Parents stock up in the last two weeks of August, from stationery and shoes to haircuts.',
    // India's school year starts in June (in-school-reopening).
    [], { excludeCountries: ['IN'] }),
  global('global-world-animal-day', 'World Animal Day', '🐾', 'observance', fixed(10, 4), 3,
    'Pets are part of the family; a photo call for customers’ pets is easy engagement.'),
  global('global-halloween', 'Halloween', '🎃', 'cultural', fixed(10, 31), 10,
    'Costumes, themed treats and a spooky window bring in families and younger customers.'),
  global('global-singles-day', "Singles' Day", '🛍️', 'retail', fixed(11, 11), 5,
    'The world’s biggest online shopping day and a natural hook for a 24-hour deal.'),
  global('global-black-friday', 'Black Friday', '🖤', 'retail', table(BLACK_FRIDAY), 14,
    'Shoppers wait for it all November, so tease your deal early and be clear about what is included.'),
  global('global-small-business-saturday', 'Small Business Saturday', '🏪', 'retail', table(SMALL_BUSINESS_SATURDAY), 7,
    'The day after Black Friday belongs to independents; tell your story and invite people in.',
    // A US campaign taken up in Europe; not a day Indian shoppers keep.
    ['NL', 'DE', 'FR', 'ES', 'IT', 'BE', 'PT', 'IE']),
  global('global-small-business-saturday-uk', 'Small Business Saturday UK', '🏪', 'retail', nth(12, SAT, 1), 7,
    'The UK campaign runs on the first Saturday of December; tell your story and invite people in.',
    ['GB']),
  global('global-cyber-monday', 'Cyber Monday', '💻', 'retail', table(CYBER_MONDAY), 7,
    'Online-only deals close the Black Friday weekend; a good day for a web-shop code.'),
  global('global-giving-tuesday', 'Giving Tuesday', '🤝', 'observance', table(GIVING_TUESDAY), 5,
    'After the deals weekend, a day to show which cause you support and invite customers to join.'),
  global('global-christmas-eve', 'Christmas Eve', '🎄', 'cultural', fixed(12, 24), 7,
    'Last-minute shoppers and family dinners; post your hours and the final order cut-off.'),
  global('global-christmas-day', 'Christmas Day', '🎅', 'public_holiday', fixed(12, 25), 14,
    'The biggest gifting and dining moment of the year; plan the whole run-up, not just the day.'),
  global('global-new-years-eve', "New Year's Eve", '🥂', 'cultural', fixed(12, 31), 7,
    'Parties, dinners and countdown menus sell out early, so open bookings well ahead.'),

  // Calendar-based observances: dates depend on moon sighting and may shift a day.
  global('global-ramadan-start', 'Ramadan begins', '🌙', 'observance', table(RAMADAN), 10,
    'Muslim customers move to evening meals and family gatherings for a month; iftar offers and adjusted hours matter.' + MOON,
    [], { durationDays: 30 }),
  global('global-ramadan-start-2', 'Ramadan begins', '🌙', 'observance', table(RAMADAN_SECOND), 10,
    'Muslim customers move to evening meals and family gatherings for a month; iftar offers and adjusted hours matter.' + MOON,
    [], { durationDays: 30 }),
  { ...global('global-eid-al-fitr', 'Eid al-Fitr', '🌙', 'cultural', table(EID_AL_FITR), 10,
    'The end of Ramadan brings new clothes, sweets, gifts and big family meals.' + MOON,
    [], { durationDays: 3 }), countryDates: { IN: IN_EID_AL_FITR } },
  { ...global('global-eid-al-adha', 'Eid al-Adha', '🐑', 'cultural', table(EID_AL_ADHA), 10,
    'A festival of sharing food with family and neighbours; butchers, caterers and gift shops are busy.' + MOON,
    [], { durationDays: 4 }), countryDates: { IN: IN_EID_AL_ADHA } },
  global('global-chinese-new-year', 'Chinese New Year', '🧧', 'cultural', table(CHINESE_NEW_YEAR), 7,
    'Red, gold and the year’s animal make an easy theme, and Asian restaurants fill for family dinners.'),
];

/* ------------------------------------------------------------------ */
/* Shared European holidays                                            */
/* ------------------------------------------------------------------ */

const EUROPE_SHARED: OccasionDefinition[] = [
  holiday('eu-good-friday', 'Good Friday', '✝️', ['NL', 'DE', 'ES', 'PT', 'IE', 'GB'], easter(-2), 5,
    'Shops close or run short hours in many places, and it opens a long weekend of family time.'),
  holiday('eu-easter-monday', 'Easter Monday', '🐰', ['NL', 'DE', 'FR', 'IT', 'BE', 'IE', 'GB'], easter(1), 5,
    'A day off for most; post your hours and expect a quiet trading day.'),
  holiday('eu-labour-day', 'Labour Day', '⚒️', ['DE', 'FR', 'ES', 'IT', 'BE', 'PT'], fixed(5, 1), 5,
    'A public holiday across most of Europe; post your hours and expect a quiet day of trade.'),
  holiday('eu-ascension-day', 'Ascension Day', '⛪', ['NL', 'DE', 'FR', 'BE'], easter(39), 5,
    'A Thursday off that many bridge into a four-day weekend of trips and garden centres.'),
  holiday('eu-whit-monday', 'Whit Monday', '🕊️', ['NL', 'DE', 'FR', 'BE'], easter(50), 5,
    'The last long weekend before summer; terraces and day trips do well.'),
  holiday('eu-assumption-day', 'Assumption Day', '⛪', ['FR', 'ES', 'BE', 'PT'], fixed(8, 15), 5,
    'A mid-summer public holiday; many are on holiday, so keep posts light and useful.'),
  holiday('eu-all-saints-day', "All Saints' Day", '🕯️', ['FR', 'ES', 'IT', 'BE', 'PT'], fixed(11, 1), 5,
    'Families visit graves with chrysanthemums; florists peak and most shops close.'),
  holiday('eu-armistice-day', 'Armistice Day', '🌹', ['FR', 'BE'], fixed(11, 11), 3,
    'A public holiday of remembrance; keep it respectful and skip promotions on the day.'),
  holiday('eu-immaculate-conception', 'Immaculate Conception', '⛪', ['ES', 'IT', 'PT'], fixed(12, 8), 5,
    'A public holiday that opens the Christmas shopping season in Spain, Italy and Portugal.'),
  regional('gb-ie-pancake-day', 'Pancake Day (Shrove Tuesday)', '🥞', 'cultural', ['GB', 'IE'], easter(-47), 5,
    'Everyone makes pancakes tonight; cafes, grocers and anyone selling lemons and toppings can join in.'),
  regional('nl-be-sint-maarten', 'Sint-Maarten', '🏮', 'cultural', ['NL', 'BE'], fixed(11, 11), 3,
    'Children walk door to door with lanterns singing for sweets; a bowl by the counter is goodwill.'),
];

/* ------------------------------------------------------------------ */
/* Netherlands                                                         */
/* ------------------------------------------------------------------ */

const NL: OccasionDefinition[] = [
  regional('nl-carnival', 'Carnaval', '🎭', 'cultural', ['NL'], easter(-49), 10,
    'Limburg and Brabant shut down for three days of parades and costumes; bars and caterers thrive.',
    { regions: ['NL-LI', 'NL-NB'], durationDays: 3 }),
  holiday('nl-kings-day', "King's Day (Koningsdag)", '🧡', ['NL'], fixed(4, 27), 10,
    'The whole country dresses in orange for street parties and flea markets, and every shop joins in.'),
  regional('nl-remembrance-day', 'Remembrance Day (Dodenherdenking)', '🕯️', 'observance', ['NL'], fixed(5, 4), 2,
    'Two minutes of silence at 20:00; keep promotional posts off the feed that evening.'),
  holiday('nl-liberation-day', 'Liberation Day (Bevrijdingsdag)', '🎗️', ['NL'], fixed(5, 5), 5,
    'Free festivals in every province and a day off for many; a good day for open-air offers.'),
  regional('nl-sinterklaas-arrival', 'Sinterklaas arrives (intocht)', '🚢', 'cultural', ['NL'],
    table({ '2026': '2026-11-14', '2027': '2027-11-13', '2028': '2028-11-18', '2029': '2029-11-17', '2030': '2030-11-16' }), 7,
    'The steamboat arrival kicks off three weeks of pepernoten, gift lists and children’s events.'),
  regional('nl-sinterklaas', 'Sinterklaas (pakjesavond)', '🎁', 'cultural', ['NL'], fixed(12, 5), 14,
    'Pakjesavond is the biggest gift night of the year for Dutch families, bigger than Christmas for many.'),
  holiday('nl-second-christmas-day', 'Second Christmas Day (Tweede Kerstdag)', '🎄', ['NL'], fixed(12, 26), 7,
    'A second day off; furniture stores and outlets famously open for the year’s busiest browsing.'),
];

/* ------------------------------------------------------------------ */
/* Germany                                                             */
/* ------------------------------------------------------------------ */

const DE: OccasionDefinition[] = [
  holiday('de-epiphany', 'Epiphany (Heilige Drei Könige)', '👑', ['DE'], fixed(1, 6), 5,
    'A public holiday in the south; the last day of the Christmas season and of most markets.',
    { regions: ['DE-BW', 'DE-BY', 'DE-ST'] }),
  regional('de-rosenmontag', 'Karneval (Rosenmontag)', '🎭', 'cultural', ['DE'], easter(-48), 10,
    'Cologne, Düsseldorf and Mainz shut down for parades; elsewhere it is a good day for playful posts.',
    { durationDays: 2 }),
  holiday('de-corpus-christi', 'Corpus Christi (Fronleichnam)', '⛪', ['DE'], easter(60), 5,
    'A Thursday holiday in the south and west that many bridge into a long weekend.',
    { regions: ['DE-BW', 'DE-BY', 'DE-HE', 'DE-NW', 'DE-RP', 'DE-SL'] }),
  holiday('de-assumption-day', 'Assumption Day (Mariä Himmelfahrt)', '⛪', ['DE'], fixed(8, 15), 3,
    'A public holiday in Saarland and much of Bavaria; post your hours.',
    { regions: ['DE-BY', 'DE-SL'] }),
  regional('de-oktoberfest', 'Oktoberfest', '🍺', 'cultural', ['DE'],
    table({ '2026': '2026-09-19', '2027': '2027-09-18', '2028': '2028-09-16', '2029': '2029-09-22', '2030': '2030-09-21' }), 14,
    'Munich’s beer festival sets the tone for Bavarian menus, dirndl windows and themed evenings nationwide.',
    { durationDays: 16 }),
  holiday('de-unity-day', 'German Unity Day (Tag der Deutschen Einheit)', '🇩🇪', ['DE'], fixed(10, 3), 5,
    'The national holiday; shops close and many take a long weekend when it falls near one.'),
  holiday('de-reformation-day', 'Reformation Day', '⛪', ['DE'], fixed(10, 31), 3,
    'A public holiday in the north and east, on the same day as Halloween.',
    { regions: ['DE-BB', 'DE-HB', 'DE-HH', 'DE-MV', 'DE-NI', 'DE-SH', 'DE-SN', 'DE-ST', 'DE-TH'] }),
  holiday('de-all-saints-day', "All Saints' Day (Allerheiligen)", '🕯️', ['DE'], fixed(11, 1), 3,
    'A public holiday in the south and west; florists peak and shops close.',
    { regions: ['DE-BW', 'DE-BY', 'DE-NW', 'DE-RP', 'DE-SL'] }),
  regional('de-carnival-kickoff', 'Carnival season opens (11.11.)', '🎉', 'cultural', ['DE'], fixed(11, 11), 3,
    'Carnival season starts at 11:11 in the Rhineland, and bars and clubs fill up for the day.'),
  regional('de-first-advent', 'First Advent', '🕯️', 'cultural', ['DE'],
    table({ '2026': '2026-11-29', '2027': '2027-11-28', '2028': '2028-12-03', '2029': '2029-12-02', '2030': '2030-12-01' }), 7,
    'Christmas markets and Advent calendars start now, and shops switch fully into seasonal mode.'),
  regional('de-nikolaus', 'Nikolaus', '🥾', 'cultural', ['DE'], fixed(12, 6), 7,
    'Children leave a boot out overnight; bakeries, sweet shops and florists sell small gifts all week.'),
  holiday('de-second-christmas-day', 'Second Christmas Day (2. Weihnachtstag)', '🎄', ['DE'], fixed(12, 26), 7,
    'A second day off spent with family; restaurants that open are full.'),
];

/* ------------------------------------------------------------------ */
/* France                                                              */
/* ------------------------------------------------------------------ */

const FR: OccasionDefinition[] = [
  regional('fr-galette-des-rois', 'Epiphany (Galette des Rois)', '👑', 'cultural', ['FR'], fixed(1, 6), 7,
    'Bakeries sell galettes all January, and offices and friends share one to find the fève.'),
  regional('fr-winter-sales', 'Winter sales (soldes d’hiver)', '🏷️', 'retail', ['FR'],
    table({ '2026': '2026-01-07', '2027': '2027-01-06', '2028': '2028-01-12', '2029': '2029-01-10', '2030': '2030-01-09' }), 7,
    'Sales are regulated and start the same day nationwide, so shoppers are ready and waiting.'),
  regional('fr-chandeleur', 'Chandeleur', '🥞', 'cultural', ['FR'], fixed(2, 2), 5,
    'Crêpe day across France; cafes, crêperies and food shops lean in.'),
  regional('fr-grandmothers-day', "Grandmothers' Day (Fête des Grands-Mères)", '👵', 'cultural', ['FR'], nth(3, SUN, 1), 7,
    'Florists, chocolatiers and restaurants get a burst of orders for grandmothers.'),
  regional('fr-mardi-gras', 'Mardi Gras', '🎭', 'cultural', ['FR'], easter(-47), 5,
    'Crêpes, beignets and costumes, with the Nice carnival drawing the crowds.'),
  holiday('fr-victory-day', 'Victory in Europe Day', '🕊️', ['FR'], fixed(5, 8), 3,
    'A public holiday of remembrance; post your hours and keep the tone respectful.'),
  regional('fr-fete-de-la-musique', 'Fête de la Musique', '🎶', 'cultural', ['FR'], fixed(6, 21), 7,
    'Free concerts on every corner keep people out late, and bars and restaurants stay open.'),
  regional('fr-summer-sales', 'Summer sales (soldes d’été)', '🏷️', 'retail', ['FR'],
    table({ '2026': '2026-06-24', '2027': '2027-06-23', '2028': '2028-06-28', '2029': '2029-06-27', '2030': '2030-06-26' }), 7,
    'Sales are regulated and start the same day nationwide, so shoppers are ready and waiting.'),
  holiday('fr-bastille-day', 'Bastille Day (Fête Nationale)', '🇫🇷', ['FR'], fixed(7, 14), 7,
    'Fireworks, dances and a national day off; terraces are full and shops close.'),
  regional('fr-beaujolais-nouveau', 'Beaujolais Nouveau', '🍷', 'cultural', ['FR'], nth(11, THU, 3), 7,
    'The new wine arrives at midnight; restaurants and wine shops build an evening around it.'),
];

/* ------------------------------------------------------------------ */
/* Spain                                                               */
/* ------------------------------------------------------------------ */

const ES: OccasionDefinition[] = [
  holiday('es-three-kings', 'Three Kings (Reyes)', '👑', ['ES'], fixed(1, 6), 10,
    'Gifts arrive with the Kings on the 6th, and the parade on the evening of the 5th fills every high street.'),
  regional('es-january-sales', 'January sales (rebajas)', '🏷️', 'retail', ['ES'], fixed(1, 7), 5,
    'Sales traditionally start the day after Reyes, when shoppers are ready to spend gift money.'),
  holiday('es-andalusia-day', 'Andalusia Day', '🟢', ['ES'], fixed(2, 28), 3,
    'A regional holiday with schools closed; post your hours.',
    { regions: ['ES-AN'] }),
  regional('es-carnival', 'Carnaval', '🎭', 'cultural', ['ES'], easter(-47), 7,
    'Cádiz and Tenerife lead the celebrations; costumes and themed nights work everywhere.'),
  regional('es-fallas', 'Las Fallas', '🔥', 'cultural', ['ES'], fixed(3, 15), 10,
    'Valencia fills with visitors, fireworks and street food for five days.',
    { regions: ['ES-VC'], durationDays: 5 }),
  holiday('es-maundy-thursday', 'Maundy Thursday (Jueves Santo)', '✝️', ['ES'], easter(-3), 7,
    'Semana Santa begins; processions and travel empty city centres for the long weekend.'),
  regional('es-sant-jordi', 'Sant Jordi', '🌹', 'cultural', ['ES'], fixed(4, 23), 7,
    'Catalonia’s day of roses and books; florists, bookshops and cafes have their busiest day.',
    { regions: ['ES-CT'] }),
  regional('es-sant-joan', 'Sant Joan', '🎆', 'cultural', ['ES'], fixed(6, 23), 5,
    'Bonfires and fireworks on the night of the 23rd, with the 24th a holiday in Catalonia.',
    { regions: ['ES-CT'] }),
  regional('es-san-fermin', 'San Fermín', '🐂', 'cultural', ['ES'], fixed(7, 6), 10,
    'Pamplona hosts a week of runs, parades and all-night crowds in white and red.',
    { regions: ['ES-NC'], durationDays: 9 }),
  regional('es-tomatina', 'La Tomatina', '🍅', 'cultural', ['ES'], nth(8, WED, -1), 5,
    'Buñol’s tomato fight draws visitors from everywhere; a fun local hook.',
    { regions: ['ES-VC'] }),
  holiday('es-diada', 'Diada de Catalunya', '🎗️', ['ES'], fixed(9, 11), 3,
    'Catalonia’s national day; shops close and the streets fill.',
    { regions: ['ES-CT'] }),
  holiday('es-hispanic-day', 'Fiesta Nacional (Día de la Hispanidad)', '🇪🇸', ['ES'], fixed(10, 12), 5,
    'The national holiday with a parade in Madrid; post your hours.'),
  holiday('es-constitution-day', 'Constitution Day', '📜', ['ES'], fixed(12, 6), 3,
    'Often joined with the 8th into a long weekend that kicks off Christmas shopping.'),
  holiday('es-sant-esteve', 'Sant Esteve', '🎄', ['ES'], fixed(12, 26), 3,
    'A second Christmas holiday in Catalonia, usually spent with the other side of the family.',
    { regions: ['ES-CT'] }),
];

/* ------------------------------------------------------------------ */
/* Italy                                                               */
/* ------------------------------------------------------------------ */

const IT: OccasionDefinition[] = [
  holiday('it-epiphany-befana', 'Epiphany (La Befana)', '🧹', ['IT'], fixed(1, 6), 7,
    'La Befana fills stockings and closes the holiday season; the last chance for gift and sweet sales.'),
  regional('it-carnevale', 'Carnevale (Martedì Grasso)', '🎭', 'cultural', ['IT'], easter(-47), 10,
    'Venice and Viareggio draw the crowds, and frittelle and masks appear everywhere.'),
  holiday('it-liberation-day', 'Liberation Day', '🇮🇹', ['IT'], fixed(4, 25), 3,
    'A public holiday that often becomes a long weekend; post your hours.'),
  holiday('it-republic-day', 'Republic Day (Festa della Repubblica)', '🇮🇹', ['IT'], fixed(6, 2), 5,
    'The national holiday, with parades and a day off that opens the summer season.'),
  regional('it-san-lorenzo', 'Night of San Lorenzo', '🌠', 'cultural', ['IT'], fixed(8, 10), 5,
    'The night of shooting stars; restaurants and beach clubs host late dinners under the sky.'),
  holiday('it-ferragosto', 'Ferragosto', '🏖️', ['IT'], fixed(8, 15), 7,
    'The peak of summer holidays; cities empty, coasts fill, and many businesses close for the week.'),
  holiday('it-santo-stefano', 'Santo Stefano', '🎄', ['IT'], fixed(12, 26), 3,
    'A second Christmas holiday spent with family and leftovers; restaurants that open are full.'),
];

/* ------------------------------------------------------------------ */
/* Belgium                                                             */
/* ------------------------------------------------------------------ */

const BE: OccasionDefinition[] = [
  regional('be-winter-sales', 'Winter sales (solden)', '🏷️', 'retail', ['BE'], fixed(1, 3), 5,
    'Belgian sales are regulated and start on 3 January, so shoppers are ready.'),
  regional('be-carnival', 'Carnival (Binche, Aalst)', '🎭', 'cultural', ['BE'], easter(-49), 10,
    'Binche’s Gilles and Aalst’s floats mark three days of parades and parties.',
    { durationDays: 3 }),
  holiday('be-flemish-community-day', 'Flemish Community Day', '🦁', ['BE'], fixed(7, 11), 3,
    'A day off for Flemish public services; post your hours.',
    { regions: ['BE-VLG'] }),
  holiday('be-national-day', 'Belgian National Day', '🇧🇪', ['BE'], fixed(7, 21), 5,
    'Fireworks in Brussels and a day off nationwide; terraces are busy.'),
  holiday('be-french-community-day', 'French Community Day', '🐓', ['BE'], fixed(9, 27), 3,
    'A day off for Walloon public services; post your hours.',
    { regions: ['BE-WAL'] }),
  regional('be-sinterklaas', 'Sinterklaas', '🎁', 'cultural', ['BE'], fixed(12, 6), 14,
    'Belgian children wake to gifts on the 6th; toy shops and bakeries peak the week before.'),
];

/* ------------------------------------------------------------------ */
/* Portugal                                                            */
/* ------------------------------------------------------------------ */

const PT: OccasionDefinition[] = [
  regional('pt-carnival', 'Carnaval', '🎭', 'cultural', ['PT'], easter(-47), 7,
    'Parades in Torres Vedras, Ovar and Loulé, and a day off for many.'),
  holiday('pt-freedom-day', 'Freedom Day (Dia da Liberdade)', '🌹', ['PT'], fixed(4, 25), 3,
    'A public holiday marking the Carnation Revolution; post your hours.'),
  holiday('pt-corpus-christi', 'Corpus Christi (Corpo de Deus)', '⛪', ['PT'], easter(60), 3,
    'A Thursday public holiday that many bridge into a long weekend.'),
  holiday('pt-portugal-day', 'Portugal Day (Dia de Portugal)', '🇵🇹', ['PT'], fixed(6, 10), 5,
    'The national holiday and the start of the Santos Populares season.'),
  regional('pt-santo-antonio', 'Santo António', '🐟', 'cultural', ['PT'], fixed(6, 13), 7,
    'Lisbon’s biggest night is the 12th: sardines, basil pots and street parties in every bairro.',
    { regions: ['PT-11'] }),
  regional('pt-sao-joao', 'São João', '🎈', 'cultural', ['PT'], fixed(6, 24), 7,
    'Porto’s night of the 23rd, with plastic hammers, balloons and grilled sardines.',
    { regions: ['PT-13'] }),
  holiday('pt-republic-day', 'Republic Day', '📜', ['PT'], fixed(10, 5), 3,
    'A public holiday; post your hours.'),
  regional('pt-sao-martinho', 'São Martinho (magusto)', '🌰', 'cultural', ['PT'], fixed(11, 11), 5,
    'Roasted chestnuts and new wine make a natural evening for restaurants and cafes.'),
  holiday('pt-restoration-day', 'Restoration of Independence', '📜', ['PT'], fixed(12, 1), 3,
    'A public holiday that opens the Christmas shopping season.'),
];

/* ------------------------------------------------------------------ */
/* Ireland                                                             */
/* ------------------------------------------------------------------ */

const IE: OccasionDefinition[] = [
  regional('ie-womens-christmas', "Women's Christmas (Nollaig na mBan)", '👩', 'cultural', ['IE'], fixed(1, 6), 5,
    'Women go out with friends while the family clears up; restaurants and bars fill.'),
  holiday('ie-st-brigids-day', "St Brigid's Day", '🌾', ['IE'],
    table({ '2026': '2026-02-02', '2027': '2027-02-01', '2028': '2028-02-07', '2029': '2029-02-05', '2030': '2030-02-01' }), 5,
    'The newest bank holiday marks the start of spring; a long weekend for many.'),
  holiday('ie-st-patricks-day', "St Patrick's Day", '☘️', ['IE'], fixed(3, 17), 10,
    'Parades, green everything and the busiest pub day of the year.'),
  holiday('ie-may-bank-holiday', 'May bank holiday', '🌷', ['IE'], nth(5, MON, 1), 5,
    'A long weekend; post your hours and any weekend offer.'),
  holiday('ie-june-bank-holiday', 'June bank holiday', '☀️', ['IE'], nth(6, MON, 1), 5,
    'The first summer long weekend; day trips and garden gatherings.'),
  regional('ie-bloomsday', 'Bloomsday', '📖', 'cultural', ['IE'], fixed(6, 16), 5,
    'Dublin dresses Edwardian for Joyce; cafes and bookshops join in.'),
  holiday('ie-august-bank-holiday', 'August bank holiday', '🏖️', ['IE'], nth(8, MON, 1), 5,
    'A summer long weekend; post your hours and any weekend offer.'),
  holiday('ie-october-bank-holiday', 'October bank holiday', '🍂', ['IE'], nth(10, MON, -1), 5,
    'The last long weekend before Christmas, right before Halloween.'),
  holiday('ie-st-stephens-day', "St Stephen's Day", '🎁', ['IE'], fixed(12, 26), 7,
    'Sales open and families head out; a big day for shops and pubs.'),
];

/* ------------------------------------------------------------------ */
/* United Kingdom                                                      */
/* ------------------------------------------------------------------ */

const GB: OccasionDefinition[] = [
  holiday('gb-second-january', '2 January holiday', '🎉', ['GB'], fixed(1, 2), 3,
    'A second New Year holiday in Scotland; post your hours.',
    { regions: ['GB-SCT'] }),
  regional('gb-burns-night', 'Burns Night', '🥃', 'cultural', ['GB'], fixed(1, 25), 7,
    'Haggis, whisky and poetry suppers; restaurants and bars book up.',
    { regions: ['GB-SCT'] }),
  regional('gb-st-davids-day', "St David's Day", '🌼', 'cultural', ['GB'], fixed(3, 1), 5,
    'Daffodils, leeks and Welsh cakes; a proud day for Welsh businesses.',
    { regions: ['GB-WLS'] }),
  regional('gb-st-patricks-day', "St Patrick's Day", '☘️', 'cultural', ['GB'], fixed(3, 17), 7,
    'One of the busiest pub nights of the year, and a holiday in Northern Ireland.'),
  regional('gb-st-georges-day', "St George's Day", '🏴󠁧󠁢󠁥󠁮󠁧󠁿', 'cultural', ['GB'], fixed(4, 23), 5,
    'England’s patron saint’s day; a light hook for pubs and traditional menus.'),
  holiday('gb-early-may-bank-holiday', 'Early May bank holiday', '🌷', ['GB'], nth(5, MON, 1), 5,
    'A long weekend; post your hours and any weekend offer.'),
  holiday('gb-spring-bank-holiday', 'Spring bank holiday', '☀️', ['GB'], nth(5, MON, -1), 5,
    'A long weekend at the end of May, often with half-term; families are out.'),
  regional('gb-wimbledon', 'Wimbledon', '🎾', 'cultural', ['GB'], nth(6, MON, -1), 7,
    'Strawberries and cream, Pimm’s and screens in every pub for two weeks.',
    { durationDays: 14 }),
  holiday('gb-battle-of-the-boyne', 'Battle of the Boyne', '🟠', ['GB'], fixed(7, 12), 3,
    'A public holiday in Northern Ireland; post your hours.',
    { regions: ['GB-NIR'] }),
  holiday('gb-summer-bank-holiday-scotland', 'Summer bank holiday (Scotland)', '🏖️', ['GB'], nth(8, MON, 1), 5,
    'Scotland’s summer long weekend; post your hours and any weekend offer.',
    { regions: ['GB-SCT'] }),
  holiday('gb-summer-bank-holiday', 'Summer bank holiday', '🏖️', ['GB'], nth(8, MON, -1), 5,
    'The last long weekend of summer; post your hours and any weekend offer.'),
  regional('gb-bonfire-night', 'Bonfire Night', '🎆', 'cultural', ['GB'], fixed(11, 5), 7,
    'Fireworks displays and toffee apples; a warm night for food and drink offers.'),
  regional('gb-remembrance-sunday', 'Remembrance Sunday', '🌹', 'observance', ['GB'], nth(11, SUN, 2), 3,
    'Two minutes’ silence at 11:00; a respectful post and no promotions.'),
  holiday('gb-st-andrews-day', "St Andrew's Day", '🏴󠁧󠁢󠁳󠁣󠁴󠁿', ['GB'], fixed(11, 30), 5,
    'Scotland’s national day; ceilidhs and Scottish menus.',
    { regions: ['GB-SCT'] }),
  holiday('gb-boxing-day', 'Boxing Day', '🎁', ['GB'], fixed(12, 26), 7,
    'Boxing Day sales are the year’s biggest bargain hunt, online and in store.'),
];

/* ------------------------------------------------------------------ */
/* India                                                               */
/* ------------------------------------------------------------------ */

const IN: OccasionDefinition[] = [
  // India's own versions of two global moments (PC-34c).
  regional('in-easter', 'Easter', '🐣', 'cultural', ['IN'], easter(0), 7,
    'Church services and family feasts, above all in Kerala, Goa and the North-East; bakeries sell plum cakes and Easter eggs, and shops stay open.'),
  regional('in-school-reopening', 'Schools reopen', '🎒', 'retail', ['IN'], fixed(6, 1), 14,
    'Most Indian schools reopen in June; parents buy uniforms, bags, shoes, books and stationery from mid-May.'),
  regional('in-makar-sankranti', 'Makar Sankranti', '🪁', 'cultural', ['IN'], table(SANKRANTI), 7,
    'Kites, til sweets and the harvest turn; a warm hook for food, gifting and family brands.'),
  regional('in-lohri', 'Lohri', '🔥', 'cultural', ['IN'], fixed(1, 13), 7,
    'Bonfires, rewri, gajak and bhangra on the eve of Maghi; sweets, dhaba menus and new-born and newly-wed gifts sell.',
    // Punjab's government list keeps Lohri on 13 January.
    { regions: ['IN-PB', 'IN-HR', 'IN-CH', 'IN-HP', 'IN-DL'] }),
  regional('in-pongal', 'Pongal', '🍚', 'cultural', ['IN'], table(PONGAL), 7,
    'Tamil Nadu’s four-day harvest festival; new clothes, sweet pongal and family visits.',
    { regions: ['IN-TN', 'IN-PY'], durationDays: 4 }),
  regional('in-vasant-panchami', 'Vasant Panchami', '💛', 'cultural', ['IN'], table(VASANT_PANCHAMI), 5,
    'Spring and Saraswati puja; yellow themes, students and anything to do with learning.'),
  holiday('in-republic-day', 'Republic Day', '🇮🇳', ['IN'], fixed(1, 26), 7,
    'A national holiday with patriotic themes, and the year’s first big online sale window.'),
  regional('in-maha-shivaratri', 'Maha Shivaratri', '🔱', 'cultural', ['IN'], table(MAHA_SHIVARATRI), 5,
    'A night of fasting and temple visits; fasting-friendly menus and devotional themes.'),
  regional('in-holi', 'Holi', '🎨', 'cultural', ['IN'], table(HOLI), 10,
    'The festival of colours; sweets, white clothes and colour-themed offers sell in the week before.'),
  regional('in-ugadi', 'Ugadi', '🌿', 'cultural', ['IN'], table(UGADI), 7,
    'New Year in Karnataka, Andhra Pradesh and Telangana; new clothes, home decor and festive food.',
    { regions: ['IN-KA', 'IN-AP', 'IN-TG'] }),
  regional('in-gudi-padwa', 'Gudi Padwa', '🚩', 'cultural', ['IN'], table(UGADI), 7,
    'Marathi New Year; gold, vehicles and new-home purchases peak, and sweets sell all day.',
    { regions: ['IN-MH', 'IN-GA'] }),
  regional('in-ram-navami', 'Ram Navami', '🏹', 'cultural', ['IN'], table(RAM_NAVAMI), 5,
    'A major festival across the north; temple visits, processions and festive food.'),
  regional('in-baisakhi', 'Baisakhi', '🌾', 'cultural', ['IN'], table(BAISAKHI), 7,
    'Punjab’s harvest festival and Sikh New Year; bhangra, fairs, new clothes and big family meals.',
    { regions: ['IN-PB', 'IN-HR', 'IN-CH'] }),
  regional('in-rongali-bihu', 'Rongali Bihu', '🥁', 'cultural', ['IN'], table(RONGALI_BIHU), 7,
    'Assam’s new year week; gamosa gifts, new clothes and pitha sweets.',
    { regions: ['IN-AS'], durationDays: 7 }),
  regional('in-puthandu', 'Puthandu (Tamil New Year)', '🌺', 'cultural', ['IN'], table(PUTHANDU), 7,
    'Chithirai 1, the Tamil New Year; kolam, new clothes, temple visits and a festive vegetarian lunch.',
    { regions: ['IN-TN', 'IN-PY'] }),
  regional('in-vishu', 'Vishu', '🌼', 'cultural', ['IN'], table(VISHU), 10,
    'Kerala’s new year; the Vishukkani, kaineettam gifts, new clothes, crackers and the Vishu sadya.',
    { regions: ['IN-KL'] }),
  regional('in-poila-boishakh', 'Poila Boishakh', '🎊', 'cultural', ['IN'], table(POILA_BOISHAKH), 7,
    'The Bengali New Year; new account books (haal khata), new clothes, sweets and family meals out.',
    { regions: ['IN-WB', 'IN-TR'] }),
  holiday('in-good-friday', 'Good Friday', '✝️', ['IN'], easter(-2), 5,
    'A public holiday across India; Christian customers are at church, so a respectful note fits better than an offer.'),
  regional('in-akshaya-tritiya', 'Akshaya Tritiya', '🪙', 'retail', ['IN'], table(AKSHAYA_TRITIYA), 10,
    'The year’s biggest day for buying gold and jewellery, and an auspicious day for homes, vehicles and new ventures.'),
  holiday('in-ambedkar-jayanti', 'Ambedkar Jayanti', '📘', ['IN'], fixed(4, 14), 3,
    'A public holiday in most states; post your hours.'),
  holiday('in-independence-day', 'Independence Day', '🇮🇳', ['IN'], fixed(8, 15), 7,
    'Tricolour themes everywhere and a big sale weekend for online retail.'),
  regional('in-friendship-day', 'Friendship Day', '🤝', 'cultural', ['IN'], nth(8, SUN, 1), 5,
    'Friendship bands, group outings and gift offers on the first Sunday of August.'),
  regional('in-muharram', 'Muharram', '🌙', 'observance', ['IN'], table(IN_MUHARRAM), 3,
    'A day of mourning for Shia Muslims and a public holiday; post your hours and keep promotions off.' + MOON),
  regional('in-chingam-1', 'Chingam 1 (Malayalam New Year)', '🌾', 'cultural', ['IN'], table(CHINGAM_ONE), 5,
    'The first day of the Malayalam year and of the Onam month; a good day for new beginnings and Onam pre-orders.',
    { regions: ['IN-KL'] }),
  regional('in-milad-un-nabi', 'Milad-un-Nabi', '🌙', 'observance', ['IN'], table(IN_MILAD_UN_NABI), 5,
    'The Prophet’s birthday and a public holiday; sweets, a respectful greeting, and post your hours.' + MOON),
  regional('in-onam', 'Onam (Thiruvonam)', '🌼', 'cultural', ['IN'], table(ONAM), 10,
    'Kerala’s harvest festival; pookalam, new clothes and the Onam sadya fill every restaurant.',
    { regions: ['IN-KL'] }),
  regional('in-raksha-bandhan', 'Raksha Bandhan', '🪢', 'cultural', ['IN'], table(RAKSHA_BANDHAN), 10,
    'Siblings exchange rakhis and gifts; sweets, jewellery and courier deadlines matter.'),
  regional('in-janmashtami', 'Krishna Janmashtami', '🦚', 'cultural', ['IN'], table(JANMASHTAMI), 7,
    'Midnight celebrations, dahi handi and sweets; a big day for mithai and temple towns.'),
  regional('in-teachers-day', "Teachers' Day", '🍎', 'observance', ['IN'], fixed(9, 5), 5,
    'Students thank their teachers; schools, tutors and gift shops all have a hook.'),
  regional('in-ganesh-chaturthi', 'Ganesh Chaturthi', '🐘', 'cultural', ['IN'], table(GANESH_CHATURTHI), 10,
    'Ten days of pandals and modaks, biggest in Maharashtra; sweets and decor sell all week.',
    { durationDays: 10 }),
  holiday('in-gandhi-jayanti', 'Gandhi Jayanti', '🕊️', ['IN'], fixed(10, 2), 5,
    'A dry day nationwide; alcohol sales stop and most businesses post a tribute rather than an offer.'),
  regional('in-navratri', 'Navratri', '💃', 'cultural', ['IN'], table(NAVRATRI), 10,
    'Nine nights of garba and dandiya, biggest in Gujarat; ethnic wear, fasting menus and events sell.',
    { durationDays: 9 }),
  regional('in-durga-puja', 'Durga Puja', '🪷', 'cultural', ['IN'], table(DURGA_PUJA), 14,
    'Bengal’s biggest festival; pandal hopping, new clothes and restaurants full from Saptami to Dashami.',
    { regions: ['IN-WB', 'IN-AS', 'IN-OR', 'IN-TR'], durationDays: 4 }),
  regional('in-dussehra', 'Dussehra (Vijayadashami)', '🏹', 'cultural', ['IN'], table(DUSSEHRA), 10,
    'The close of Navratri; new purchases, vehicle and gold buying, and festive menus.'),
  regional('in-karva-chauth', 'Karva Chauth', '🌕', 'cultural', ['IN'], table(KARVA_CHAUTH), 7,
    'Married women fast until moonrise; mehendi, sarees, jewellery and gifts sell in the days before.',
    { regions: ['IN-PB', 'IN-HR', 'IN-RJ', 'IN-UP', 'IN-DL'] }),
  regional('in-dhanteras', 'Dhanteras', '🪙', 'retail', ['IN'], table(DHANTERAS), 7,
    'The auspicious day to buy gold, silver, utensils and electronics; the start of Diwali shopping.'),
  regional('in-diwali', 'Diwali', '🪔', 'cultural', ['IN', 'GB'], table(DIWALI), 14,
    'The biggest shopping and gifting season of the year; plan the whole fortnight before.',
    { durationDays: 3 }),
  regional('in-bhai-dooj', 'Bhai Dooj', '🎁', 'cultural', ['IN'], table(BHAI_DOOJ), 5,
    'Siblings’ day at the end of Diwali; sweets and gifts one last time.'),
  regional('in-kerala-piravi', 'Kerala Piravi', '🌴', 'cultural', ['IN'], fixed(11, 1), 5,
    'Kerala’s formation day; kasavu, Kerala menus and a proud local greeting.',
    { regions: ['IN-KL'] }),
  regional('in-childrens-day', "Children's Day", '🎈', 'observance', ['IN'], fixed(11, 14), 5,
    'Schools celebrate and parents buy treats; a natural day for family offers.'),
  regional('in-chhath-puja', 'Chhath Puja', '🌅', 'cultural', ['IN'], table(CHHATH), 7,
    'Four days of prayers to the sun, biggest in Bihar and eastern Uttar Pradesh; fruit, sweets and travel.',
    { regions: ['IN-BR', 'IN-JH', 'IN-UP'] }),
  regional('in-guru-nanak-jayanti', 'Guru Nanak Jayanti', '🙏', 'cultural', ['IN'], table(GURU_NANAK_JAYANTI), 7,
    'Gurpurab, the birth of Guru Nanak; langar, processions and lights, biggest in Punjab.'),
];

/* ------------------------------------------------------------------ */
/* The Gulf and the Arab world                                          */
/* ------------------------------------------------------------------ */

const ARAB: OccasionDefinition[] = [
  // Islamic calendar. Eid and Ramadan are global entries above.
  regional('arab-day-of-arafat', 'Day of Arafat', '🕋', 'observance', ARAB_COUNTRIES, table(DAY_OF_ARAFAT), 7,
    'The eve of Eid al-Adha and a public holiday across the Gulf; post your hours and Eid offers.' + MOON),
  regional('arab-islamic-new-year', 'Islamic New Year', '🌙', 'observance', ARAB_COUNTRIES, table(ISLAMIC_NEW_YEAR), 7,
    'A public holiday in most of the region; a calm, respectful greeting suits it better than an offer.' + MOON),
  regional('arab-mawlid', 'Prophet’s Birthday (Mawlid)', '🌙', 'observance', ['AE', 'KW', 'BH', 'OM', 'EG', 'JO', 'MA'], table(MAWLID), 7,
    'A public holiday in much of the region; sweets and a respectful greeting, and post your hours.' + MOON),
  regional('arab-isra-miraj', 'Isra and Mi’raj', '🌙', 'observance', ['KW', 'OM', 'JO'], table(ISRA_MIRAJ), 5,
    'A public holiday in Kuwait, Oman and Jordan; post your hours.' + MOON),
  regional('arab-isra-miraj-2', 'Isra and Mi’raj', '🌙', 'observance', ['KW', 'OM', 'JO'], table(ISRA_MIRAJ_SECOND), 5,
    'A public holiday in Kuwait, Oman and Jordan; post your hours.' + MOON),
  // Retail moments
  regional('arab-white-friday', 'White Friday', '🛍️', 'retail', GULF, table(BLACK_FRIDAY), 14,
    'The Gulf’s name for Black Friday; the biggest online sale week of the year.'),
  regional('arab-mothers-day', "Mother's Day", '💐', 'cultural', ['EG', 'JO', 'AE', 'QA', 'KW', 'BH', 'OM'], fixed(3, 21), 10,
    'Mothers are celebrated on the first day of spring; flowers, gifts, perfume and restaurants.'),
  // Saudi Arabia
  holiday('sa-founding-day', 'Founding Day', '🇸🇦', ['SA'], fixed(2, 22), 10,
    'A national holiday celebrating the first Saudi state; heritage themes and big weekend offers.'),
  regional('sa-flag-day', 'Flag Day', '🇸🇦', 'observance', ['SA'], fixed(3, 11), 5,
    'A day to show the flag; green themes suit it, though it is not a day off.'),
  holiday('sa-national-day', 'Saudi National Day', '🇸🇦', ['SA'], fixed(9, 23), 14,
    'Green everywhere and one of the biggest sale weeks of the year.'),
  // United Arab Emirates
  holiday('ae-national-day', 'UAE National Day', '🇦🇪', ['AE'], fixed(12, 2), 14,
    'Eid Al Etihad: flags, heritage and the year’s big long-weekend offers.',
    { durationDays: 2 }),
  // Qatar
  holiday('qa-national-sport-day', 'National Sport Day', '🏃', ['QA'], nth(2, TUE, 2), 7,
    'A public holiday for sport and activity; gyms, sportswear and outdoor events all have a hook.'),
  holiday('qa-national-day', 'Qatar National Day', '🇶🇦', ['QA'], fixed(12, 18), 14,
    'Maroon and white, the national day parade and a big week for offers.'),
  // Kuwait
  holiday('kw-national-day', 'Kuwait National Day', '🇰🇼', ['KW'], fixed(2, 25), 10,
    'The start of two days of national celebration; flags, family outings and offers.'),
  holiday('kw-liberation-day', 'Liberation Day', '🇰🇼', ['KW'], fixed(2, 26), 10,
    'The second day of the national holidays; keep the same celebratory tone.'),
  // Bahrain
  holiday('bh-national-day', 'Bahrain National Day', '🇧🇭', ['BH'], fixed(12, 16), 14,
    'Two days of national celebration; red and white themes and holiday offers.',
    { durationDays: 2 }),
  // Oman
  holiday('om-national-day', 'Oman National Day', '🇴🇲', ['OM'], fixed(11, 20), 14,
    'A national holiday with flags and celebrations across the country.',
    { durationDays: 2 }),
  // Egypt
  holiday('eg-coptic-christmas', 'Coptic Christmas', '🎄', ['EG'], fixed(1, 7), 7,
    'A public holiday; greetings to Coptic customers and festive family meals.'),
  holiday('eg-january-25', 'Revolution Day and Police Day', '🇪🇬', ['EG'], fixed(1, 25), 5,
    'A public holiday; post your hours.'),
  holiday('eg-sham-el-nessim', 'Sham el-Nessim', '🌸', ['EG'], table(SHAM_EL_NESSIM), 7,
    'Egypt’s spring festival; picnics, coloured eggs and feseekh, and everyone is out of the house.'),
  holiday('eg-sinai-liberation', 'Sinai Liberation Day', '🇪🇬', ['EG'], fixed(4, 25), 5,
    'A public holiday; post your hours.'),
  holiday('eg-june-30', 'June 30 Revolution Day', '🇪🇬', ['EG'], fixed(6, 30), 5,
    'A public holiday; post your hours.'),
  holiday('eg-revolution-day', 'Revolution Day', '🇪🇬', ['EG'], fixed(7, 23), 5,
    'A national holiday; patriotic greetings and summer offers.'),
  holiday('eg-armed-forces-day', 'Armed Forces Day', '🇪🇬', ['EG'], fixed(10, 6), 5,
    'A public holiday; a respectful national greeting suits it.'),
  // Jordan
  holiday('jo-independence-day', 'Independence Day', '🇯🇴', ['JO'], fixed(5, 25), 10,
    'Jordan’s national day; flags, family outings and holiday offers.'),
  // Morocco
  holiday('ma-independence-manifesto', 'Independence Manifesto Day', '🇲🇦', ['MA'], fixed(1, 11), 5,
    'A public holiday; post your hours.'),
  holiday('ma-amazigh-new-year', 'Amazigh New Year (Yennayer)', 'ⵣ', ['MA'], fixed(1, 14), 7,
    'A public holiday since 2024; traditional dishes and Amazigh heritage themes.'),
  holiday('ma-throne-day', 'Throne Day', '🇲🇦', ['MA'], fixed(7, 30), 10,
    'Morocco’s national day; flags, celebrations and a summer holiday mood.'),
  holiday('ma-green-march', 'Green March', '🇲🇦', ['MA'], fixed(11, 6), 5,
    'A public holiday; post your hours.'),
  holiday('ma-independence-day', 'Independence Day', '🇲🇦', ['MA'], fixed(11, 18), 7,
    'A national holiday; patriotic greetings and weekend offers.'),
];

/* ------------------------------------------------------------------ */
/* Industry                                                            */
/* ------------------------------------------------------------------ */

const INDUSTRY: OccasionDefinition[] = [
  // Food, hospitality
  industry('industry-pizza-day', 'International Pizza Day', '🍕', ['restaurant'], fixed(2, 9), 5,
    'An easy excuse for a pizza deal or a behind-the-oven post.'),
  industry('industry-cocktail-day', 'World Cocktail Day', '🍸', ['restaurant', 'hospitality'], fixed(5, 13), 5,
    'Show off a signature cocktail or run a happy hour.'),
  industry('industry-tea-day', 'International Tea Day', '🍵', ['restaurant', 'retail'], fixed(5, 21), 3,
    'A gentle hook for cafes and tea shops, and a big one in India.'),
  industry('industry-sushi-day', 'International Sushi Day', '🍣', ['restaurant'], fixed(6, 18), 5,
    'Sushi bars and Japanese restaurants get an easy themed day.'),
  industry('industry-chocolate-day', 'World Chocolate Day', '🍫', ['restaurant', 'retail'], fixed(7, 7), 5,
    'Chocolatiers, bakeries and cafes can feature a chocolate special.'),
  industry('industry-beer-day', 'International Beer Day', '🍺', ['restaurant', 'hospitality'], nth(8, FRI, 1), 5,
    'Craft beer tastings and a Friday-night hook for bars.'),
  industry('industry-tourism-day', 'World Tourism Day', '🧳', ['hospitality'], fixed(9, 27), 7,
    'Hotels, guides and tour operators can share what makes their place worth the trip.'),
  industry('industry-coffee-day', 'International Coffee Day', '☕', ['restaurant', 'hospitality', 'retail'], fixed(10, 1), 5,
    'Cafes and roasters run a free-coffee hour or feature a single origin.'),
  industry('industry-world-food-day', 'World Food Day', '🥗', ['restaurant'], fixed(10, 16), 5,
    'A day to talk about local sourcing, waste reduction and community meals.'),
  industry('industry-pasta-day', 'World Pasta Day', '🍝', ['restaurant'], fixed(10, 25), 5,
    'Italian restaurants and delis can put a pasta dish or a fresh-pasta class up front.'),
  industry('industry-vegan-day', 'World Vegan Day', '🌿', ['restaurant'], fixed(11, 1), 5,
    'A good day to show your plant-based options to new customers.'),

  // Beauty, wellness
  industry('industry-lipstick-day', 'National Lipstick Day', '💄', ['beauty'], fixed(7, 29), 5,
    'Beauty brands and salons feature a shade of the day or a mini makeover.'),
  industry('industry-skin-care-awareness', 'Skin Care Awareness Month', '🧴', ['beauty'], fixed(9, 1), 7,
    'A month to educate on routines and book consultations after summer.',
    { durationDays: 30 }),
  industry('industry-beauty-day', 'International Beauty Day', '✨', ['beauty'], fixed(9, 9), 5,
    'Salons and beauty shops can celebrate their clients and their craft.'),
  industry('industry-global-wellness-day', 'Global Wellness Day', '🧘', ['beauty', 'fitness', 'hospitality'], nth(6, SAT, 2), 7,
    'Spas, gyms and studios host free sessions and wellness offers on the second Saturday of June.'),

  // Fashion (fashion weeks are nominal dates; exact schedules are announced each season)
  industry('industry-london-fashion-week-feb', 'London Fashion Week (nominal)', '👗', ['fashion'], fixed(2, 20), 7,
    'Fashion week season sets the trends your customers will ask for; share your take.',
    { durationDays: 5 }),
  industry('industry-milan-fashion-week-feb', 'Milan Fashion Week (nominal)', '👠', ['fashion'], fixed(2, 24), 7,
    'Fashion week season sets the trends your customers will ask for; share your take.',
    { durationDays: 6 }),
  industry('industry-paris-fashion-week-mar', 'Paris Fashion Week (nominal)', '🎀', ['fashion'], fixed(3, 1), 7,
    'Fashion week season sets the trends your customers will ask for; share your take.',
    { durationDays: 9 }),
  industry('industry-fashion-revolution-week', 'Fashion Revolution Week', '♻️', ['fashion'], fixed(4, 24), 7,
    'Customers ask who made their clothes; a good week to show your suppliers and values.',
    { durationDays: 7 }),
  industry('industry-london-fashion-week-sep', 'London Fashion Week (nominal)', '👗', ['fashion'], fixed(9, 18), 7,
    'Fashion week season sets the trends your customers will ask for; share your take.',
    { durationDays: 5 }),
  industry('industry-milan-fashion-week-sep', 'Milan Fashion Week (nominal)', '👠', ['fashion'], fixed(9, 22), 7,
    'Fashion week season sets the trends your customers will ask for; share your take.',
    { durationDays: 6 }),
  industry('industry-paris-fashion-week-sep', 'Paris Fashion Week (nominal)', '🎀', ['fashion'], fixed(9, 28), 7,
    'Fashion week season sets the trends your customers will ask for; share your take.',
    { durationDays: 9 }),

  // Fitness
  industry('industry-new-year-resolutions', 'New Year resolutions', '💪', ['fitness'], fixed(1, 2), 10,
    'The busiest sign-up fortnight of the year for gyms and studios; make joining easy.',
    { durationDays: 14 }),
  industry('industry-world-bicycle-day', 'World Bicycle Day', '🚲', ['fitness'], fixed(6, 3), 5,
    'Group rides and bike-to-work offers.'),
  industry('industry-global-running-day', 'Global Running Day', '🏃', ['fitness'], nth(6, WED, 1), 5,
    'Host a community run or a free trial class.'),
  industry('industry-yoga-day', 'International Yoga Day', '🧘', ['fitness', 'healthcare'], fixed(6, 21), 7,
    'Open-air classes and free sessions, especially big in India.'),
  industry('industry-world-heart-day', 'World Heart Day', '❤️', ['healthcare', 'fitness'], fixed(9, 29), 5,
    'A day for check-up reminders and heart-healthy tips.'),

  // E-commerce, retail
  industry('industry-prime-day', 'Prime Day (nominal)', '📦', ['ecommerce', 'retail'], fixed(7, 15), 7,
    'Shoppers expect mid-July deals everywhere; ride the wave with your own offer.',
    { durationDays: 2 }),
  industry('industry-green-monday', 'Green Monday', '🛒', ['ecommerce', 'retail'], nth(12, MON, 2), 5,
    'The last big online shopping Monday before Christmas delivery cut-offs.'),
  industry('industry-free-shipping-day', 'Free Shipping Day (nominal)', '🚚', ['ecommerce'], fixed(12, 14), 5,
    'Guaranteed-by-Christmas free shipping nudges late online shoppers.'),

  // Education
  industry('industry-education-day', 'International Day of Education', '📚', ['education'], fixed(1, 24), 5,
    'A day to share your teaching story and open enrolments.'),
  industry('industry-world-book-day', 'World Book Day', '📖', ['education', 'retail'], fixed(4, 23), 7,
    'Book swaps, reading challenges and bookshop offers.'),
  industry('industry-literacy-day', 'International Literacy Day', '✏️', ['education'], fixed(9, 8), 5,
    'Schools, tutors and libraries can share a reading initiative.'),
  industry('industry-world-teachers-day', "World Teachers' Day", '🍎', ['education'], fixed(10, 5), 5,
    'Thank your teachers publicly and invite parents to join in.'),

  // Healthcare
  industry('industry-oral-health-day', 'World Oral Health Day', '🦷', ['healthcare'], fixed(3, 20), 5,
    'Dentists can run check-up reminders and hygiene tips.'),
  industry('industry-world-health-day', 'World Health Day', '🩺', ['healthcare', 'fitness'], fixed(4, 7), 5,
    'A broad health day for clinics, pharmacies and wellness businesses.'),
  industry('industry-nurses-day', 'International Nurses Day', '🩹', ['healthcare'], fixed(5, 12), 5,
    'Thank your nursing staff and show the people behind the care.'),
  industry('industry-no-tobacco-day', 'World No Tobacco Day', '🚭', ['healthcare'], fixed(5, 31), 5,
    'Clinics and pharmacies can promote quit-smoking support.'),
  industry('industry-breast-cancer-awareness', 'Breast Cancer Awareness Month', '🎀', ['healthcare', 'beauty'], fixed(10, 1), 7,
    'A month of pink; screening reminders and charity partnerships.',
    { durationDays: 31 }),
  industry('industry-mental-health-day', 'World Mental Health Day', '🧠', ['healthcare'], fixed(10, 10), 5,
    'Share support resources and how your practice helps.'),
  industry('industry-diabetes-day', 'World Diabetes Day', '💙', ['healthcare'], fixed(11, 14), 5,
    'Screening offers and diet advice from clinics and pharmacies.'),

  // Real estate
  industry('industry-spring-moving-season', 'Spring moving season (nominal)', '🏡', ['real_estate'], fixed(3, 15), 14,
    'Listings and viewings pick up from mid-March as buyers plan a summer move.'),
  industry('industry-autumn-listing-season', 'Autumn listing season (nominal)', '🍂', ['real_estate'], fixed(9, 1), 14,
    'The second listing peak after the summer break; sellers want to close before Christmas.'),
  industry('industry-world-habitat-day', 'World Habitat Day', '🏘️', ['real_estate'], nth(10, MON, 1), 5,
    'A day to talk about housing, neighbourhoods and the places you sell.'),

  // Technology
  industry('industry-data-privacy-day', 'Data Privacy Day', '🔐', ['technology'], fixed(1, 28), 5,
    'Explain how you protect customer data; trust sells.'),
  industry('industry-safer-internet-day', 'Safer Internet Day', '🛡️', ['technology', 'education'], nth(2, TUE, 2), 5,
    'Share practical online-safety tips for customers and families.'),
  industry('industry-world-backup-day', 'World Backup Day', '💾', ['technology'], fixed(3, 31), 5,
    'A reminder to back up, and a natural pitch for your service.'),
  industry('industry-world-password-day', 'World Password Day', '🔑', ['technology'], nth(5, THU, 1), 5,
    'Quick security tips build trust with less technical customers.'),
  industry('industry-sysadmin-day', 'System Administrator Appreciation Day', '🖥️', ['technology'], nth(7, FRI, -1), 5,
    'Thank the people who keep things running, yours and your clients’.'),
  industry('industry-programmers-day', 'Day of the Programmer', '👩‍💻', ['technology'],
    table({ '2026': '2026-09-13', '2027': '2027-09-13', '2028': '2028-09-12', '2029': '2029-09-13', '2030': '2030-09-13' }), 5,
    'The 256th day of the year; a fun one for dev teams and software shops.'),
];

/** Every built-in definition. Custom (workspace) occasions come from the service. */
export const OCCASION_DATASET: readonly OccasionDefinition[] = [
  ...GLOBAL,
  ...EUROPE_SHARED,
  ...NL,
  ...DE,
  ...FR,
  ...ES,
  ...IT,
  ...BE,
  ...PT,
  ...IE,
  ...GB,
  ...IN,
  ...ARAB,
  ...INDUSTRY,
];

/** Industry keys the dataset knows about (onboarding list plus extras). */
export const OCCASION_INDUSTRIES = [
  'technology', 'ecommerce', 'restaurant', 'healthcare', 'real_estate', 'education', 'fashion', 'other',
  'beauty', 'hospitality', 'retail', 'fitness',
] as const;
